// Gerador de QR Code local (ISO/IEC 18004), sem dependencia nova: modo byte
// (UTF-8), niveis de correcao L e M, versoes 1 a 40. Algoritmo classico
// (Reed-Solomon sobre GF(256), mascara pelo menor custo de penalidade).
// Saida: matriz booleana (true = modulo escuro), renderizada em SVG pelo
// componente QrCode. Conferido contra a lib python-qrcode nos testes.

export type QrEcc = "L" | "M";

export interface QrOptions {
  readonly ecc?: QrEcc;
  /** Forca a mascara (0-7); usado nos testes de referencia. */
  readonly mask?: number;
}

export interface QrMatrix {
  readonly version: number;
  readonly size: number;
  readonly mask: number;
  readonly modules: readonly (readonly boolean[])[];
}

// Tabelas da norma (indice = versao; posicao 0 sem uso).
const ECC_PER_BLOCK: Record<QrEcc, readonly number[]> = {
  L: [-1, 7, 10, 15, 20, 26, 18, 20, 24, 30, 18, 20, 24, 26, 30, 22, 24, 28, 30, 28, 28, 28, 28, 30, 30, 26, 28, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30],
  M: [-1, 10, 16, 26, 18, 24, 16, 18, 22, 22, 26, 30, 22, 22, 24, 24, 28, 28, 26, 26, 26, 26, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28],
};
const NUM_BLOCKS: Record<QrEcc, readonly number[]> = {
  L: [-1, 1, 1, 1, 1, 1, 2, 2, 2, 2, 4, 4, 4, 4, 4, 6, 6, 6, 6, 7, 8, 8, 9, 9, 10, 12, 12, 12, 13, 14, 15, 16, 17, 18, 19, 19, 20, 21, 22, 24, 25],
  M: [-1, 1, 1, 1, 2, 2, 4, 4, 4, 5, 5, 5, 8, 9, 9, 10, 10, 11, 13, 14, 16, 17, 17, 18, 20, 21, 23, 25, 26, 28, 29, 31, 33, 35, 37, 38, 40, 43, 45, 47, 49],
};
const FORMAT_ECC_BITS: Record<QrEcc, number> = { L: 1, M: 0 };
const PENALTY_N1 = 3;
const PENALTY_N2 = 3;
const PENALTY_N3 = 40;
const PENALTY_N4 = 10;

export function rawDataModules(version: number): number {
  let result = (16 * version + 128) * version + 64;
  if (version >= 2) {
    const numAlign = Math.floor(version / 7) + 2;
    result -= (25 * numAlign - 10) * numAlign - 55;
    if (version >= 7) result -= 36;
  }
  return result;
}

export function dataCodewords(version: number, ecc: QrEcc): number {
  return Math.floor(rawDataModules(version) / 8) - (ECC_PER_BLOCK[ecc][version] as number) * (NUM_BLOCKS[ecc][version] as number);
}

function gfMultiply(x: number, y: number): number {
  let z = 0;
  for (let i = 7; i >= 0; i--) {
    z = (z << 1) ^ ((z >>> 7) * 0x11d);
    z ^= ((y >>> i) & 1) * x;
  }
  return z;
}

function rsDivisor(degree: number): number[] {
  const result = new Array<number>(degree).fill(0);
  result[degree - 1] = 1;
  let root = 1;
  for (let i = 0; i < degree; i++) {
    for (let j = 0; j < degree; j++) {
      result[j] = gfMultiply(result[j] as number, root);
      if (j + 1 < degree) result[j] = (result[j] as number) ^ (result[j + 1] as number);
    }
    root = gfMultiply(root, 0x02);
  }
  return result;
}

function rsRemainder(data: readonly number[], divisor: readonly number[]): number[] {
  const result = divisor.map(() => 0);
  for (const byte of data) {
    const factor = byte ^ (result.shift() as number);
    result.push(0);
    divisor.forEach((coef, i) => {
      result[i] = (result[i] as number) ^ gfMultiply(coef, factor);
    });
  }
  return result;
}

function pickVersion(byteLength: number, ecc: QrEcc): number {
  for (let version = 1; version <= 40; version++) {
    const countBits = version <= 9 ? 8 : 16;
    if (4 + countBits + byteLength * 8 <= dataCodewords(version, ecc) * 8) return version;
  }
  throw new Error("Texto longo demais para um QR Code.");
}

function buildDataCodewords(bytes: Uint8Array, version: number, ecc: QrEcc): number[] {
  const bits: number[] = [];
  const push = (value: number, length: number): void => {
    for (let i = length - 1; i >= 0; i--) bits.push((value >>> i) & 1);
  };
  push(0b0100, 4);
  push(bytes.length, version <= 9 ? 8 : 16);
  bytes.forEach((byte) => push(byte, 8));
  const capacity = dataCodewords(version, ecc) * 8;
  push(0, Math.min(4, capacity - bits.length));
  push(0, (8 - (bits.length % 8)) % 8);
  for (let pad = 0xec; bits.length < capacity; pad ^= 0xec ^ 0x11) push(pad, 8);
  const codewords: number[] = [];
  for (let i = 0; i < bits.length; i += 8) {
    codewords.push(bits.slice(i, i + 8).reduce((acc, bit) => (acc << 1) | bit, 0));
  }
  return codewords;
}

function addEccAndInterleave(data: readonly number[], version: number, ecc: QrEcc): number[] {
  const numBlocks = NUM_BLOCKS[ecc][version] as number;
  const blockEccLen = ECC_PER_BLOCK[ecc][version] as number;
  const rawCodewords = Math.floor(rawDataModules(version) / 8);
  const numShortBlocks = numBlocks - (rawCodewords % numBlocks);
  const shortBlockLen = Math.floor(rawCodewords / numBlocks);
  const divisor = rsDivisor(blockEccLen);
  const blocks: number[][] = [];
  for (let i = 0, k = 0; i < numBlocks; i++) {
    const dataLen = shortBlockLen - blockEccLen + (i < numShortBlocks ? 0 : 1);
    const blockData = data.slice(k, k + dataLen);
    k += dataLen;
    const padded = i < numShortBlocks ? [...blockData, 0] : [...blockData];
    blocks.push([...padded, ...rsRemainder(blockData, divisor)]);
  }
  const result: number[] = [];
  for (let i = 0; i < (blocks[0] as number[]).length; i++) {
    blocks.forEach((block, j) => {
      if (i !== shortBlockLen - blockEccLen || j >= numShortBlocks) result.push(block[i] as number);
    });
  }
  return result;
}

function alignmentPositions(version: number): number[] {
  if (version === 1) return [];
  const numAlign = Math.floor(version / 7) + 2;
  const step = version === 32 ? 26 : Math.ceil((version * 4 + 4) / (numAlign * 2 - 2)) * 2;
  const result = [6];
  for (let pos = version * 4 + 10; result.length < numAlign; pos -= step) result.splice(1, 0, pos);
  return result;
}

class Grid {
  readonly modules: boolean[][];
  readonly isFunction: boolean[][];

  constructor(readonly size: number) {
    this.modules = Array.from({ length: size }, () => new Array<boolean>(size).fill(false));
    this.isFunction = Array.from({ length: size }, () => new Array<boolean>(size).fill(false));
  }

  setFunction(x: number, y: number, dark: boolean): void {
    (this.modules[y] as boolean[])[x] = dark;
    (this.isFunction[y] as boolean[])[x] = true;
  }
}

function drawFinder(grid: Grid, cx: number, cy: number): void {
  for (let dy = -4; dy <= 4; dy++) {
    for (let dx = -4; dx <= 4; dx++) {
      const dist = Math.max(Math.abs(dx), Math.abs(dy));
      const x = cx + dx;
      const y = cy + dy;
      if (x >= 0 && x < grid.size && y >= 0 && y < grid.size) grid.setFunction(x, y, dist !== 2 && dist !== 4);
    }
  }
}

function drawFunctionPatterns(grid: Grid, version: number): void {
  for (let i = 0; i < grid.size; i++) {
    grid.setFunction(6, i, i % 2 === 0);
    grid.setFunction(i, 6, i % 2 === 0);
  }
  drawFinder(grid, 3, 3);
  drawFinder(grid, grid.size - 4, 3);
  drawFinder(grid, 3, grid.size - 4);
  const positions = alignmentPositions(version);
  const last = positions.length - 1;
  positions.forEach((px, i) => positions.forEach((py, j) => {
    const isCorner = (i === 0 && j === 0) || (i === 0 && j === last) || (i === last && j === 0);
    if (isCorner) return;
    for (let dy = -2; dy <= 2; dy++) {
      for (let dx = -2; dx <= 2; dx++) grid.setFunction(px + dx, py + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1);
    }
  }));
  drawFormatBits(grid, "M", 0);
  drawVersion(grid, version);
}

function bit(value: number, index: number): boolean {
  return ((value >>> index) & 1) !== 0;
}

function drawFormatBits(grid: Grid, ecc: QrEcc, mask: number): void {
  const data = (FORMAT_ECC_BITS[ecc] << 3) | mask;
  let rem = data;
  for (let i = 0; i < 10; i++) rem = (rem << 1) ^ ((rem >>> 9) * 0x537);
  const bits = ((data << 10) | rem) ^ 0x5412;
  const size = grid.size;
  for (let i = 0; i <= 5; i++) grid.setFunction(8, i, bit(bits, i));
  grid.setFunction(8, 7, bit(bits, 6));
  grid.setFunction(8, 8, bit(bits, 7));
  grid.setFunction(7, 8, bit(bits, 8));
  for (let i = 9; i < 15; i++) grid.setFunction(14 - i, 8, bit(bits, i));
  for (let i = 0; i < 8; i++) grid.setFunction(size - 1 - i, 8, bit(bits, i));
  for (let i = 8; i < 15; i++) grid.setFunction(8, size - 15 + i, bit(bits, i));
  grid.setFunction(8, size - 8, true);
}

function drawVersion(grid: Grid, version: number): void {
  if (version < 7) return;
  let rem = version;
  for (let i = 0; i < 12; i++) rem = (rem << 1) ^ ((rem >>> 11) * 0x1f25);
  const bits = (version << 12) | rem;
  for (let i = 0; i < 18; i++) {
    const a = grid.size - 11 + (i % 3);
    const b = Math.floor(i / 3);
    grid.setFunction(a, b, bit(bits, i));
    grid.setFunction(b, a, bit(bits, i));
  }
}

function drawCodewords(grid: Grid, data: readonly number[]): void {
  let i = 0;
  for (let right = grid.size - 1; right >= 1; right -= 2) {
    if (right === 6) right = 5;
    for (let vert = 0; vert < grid.size; vert++) {
      for (let j = 0; j < 2; j++) {
        const x = right - j;
        const upward = ((right + 1) & 2) === 0;
        const y = upward ? grid.size - 1 - vert : vert;
        if (!(grid.isFunction[y] as boolean[])[x] && i < data.length * 8) {
          (grid.modules[y] as boolean[])[x] = bit(data[i >>> 3] as number, 7 - (i & 7));
          i++;
        }
      }
    }
  }
}

const MASKS: ReadonlyArray<(x: number, y: number) => boolean> = [
  (x, y) => (x + y) % 2 === 0,
  (_x, y) => y % 2 === 0,
  (x) => x % 3 === 0,
  (x, y) => (x + y) % 3 === 0,
  (x, y) => (Math.floor(x / 3) + Math.floor(y / 2)) % 2 === 0,
  (x, y) => ((x * y) % 2) + ((x * y) % 3) === 0,
  (x, y) => (((x * y) % 2) + ((x * y) % 3)) % 2 === 0,
  (x, y) => (((x + y) % 2) + ((x * y) % 3)) % 2 === 0,
];

function applyMask(grid: Grid, mask: number): void {
  const invert = MASKS[mask] as (x: number, y: number) => boolean;
  for (let y = 0; y < grid.size; y++) {
    for (let x = 0; x < grid.size; x++) {
      if (!(grid.isFunction[y] as boolean[])[x] && invert(x, y)) {
        (grid.modules[y] as boolean[])[x] = !(grid.modules[y] as boolean[])[x];
      }
    }
  }
}

function lineScore(line: readonly boolean[]): number {
  let score = 0;
  let runLength = 1;
  for (let i = 1; i <= line.length; i++) {
    if (i < line.length && line[i] === line[i - 1]) {
      runLength++;
      continue;
    }
    if (runLength >= 5) score += PENALTY_N1 + (runLength - 5);
    runLength = 1;
  }
  const text = line.map((dark) => (dark ? "1" : "0")).join("");
  for (const pattern of ["10111010000", "00001011101"]) {
    for (let at = text.indexOf(pattern); at !== -1; at = text.indexOf(pattern, at + 1)) score += PENALTY_N3;
  }
  return score;
}

export function penaltyScore(modules: readonly (readonly boolean[])[]): number {
  const size = modules.length;
  const columns = modules.map((_, x) => modules.map((row) => row[x] as boolean));
  let score = [...modules, ...columns].reduce((acc, line) => acc + lineScore(line), 0);
  for (let y = 0; y < size - 1; y++) {
    for (let x = 0; x < size - 1; x++) {
      const color = modules[y]?.[x];
      if (color === modules[y]?.[x + 1] && color === modules[y + 1]?.[x] && color === modules[y + 1]?.[x + 1]) score += PENALTY_N2;
    }
  }
  const dark = modules.reduce((acc, row) => acc + row.filter(Boolean).length, 0);
  const total = size * size;
  score += Math.floor(Math.abs(dark * 20 - total * 10) / total) * PENALTY_N4;
  return score;
}

/** Codifica texto (UTF-8) num QR Code. */
export function encodeQr(text: string, options: QrOptions = {}): QrMatrix {
  const ecc = options.ecc ?? "M";
  const bytes = new TextEncoder().encode(text);
  const version = pickVersion(bytes.length, ecc);
  const codewords = addEccAndInterleave(buildDataCodewords(bytes, version, ecc), version, ecc);
  const grid = new Grid(version * 4 + 17);
  drawFunctionPatterns(grid, version);
  drawCodewords(grid, codewords);

  const mask = options.mask ?? bestMask(grid, ecc);
  applyMask(grid, mask);
  drawFormatBits(grid, ecc, mask);
  return { version, size: grid.size, mask, modules: grid.modules };
}

function bestMask(grid: Grid, ecc: QrEcc): number {
  let best = 0;
  let minScore = Number.POSITIVE_INFINITY;
  for (let mask = 0; mask < MASKS.length; mask++) {
    applyMask(grid, mask);
    drawFormatBits(grid, ecc, mask);
    const score = penaltyScore(grid.modules);
    if (score < minScore) {
      minScore = score;
      best = mask;
    }
    applyMask(grid, mask);
  }
  return best;
}

/** Path SVG (1 unidade por modulo, com margem) para renderizar ou baixar. */
export function qrSvgPath(matrix: QrMatrix, margin = 4): string {
  const parts: string[] = [];
  matrix.modules.forEach((row, y) => row.forEach((dark, x) => {
    if (dark) parts.push(`M${x + margin},${y + margin}h1v1h-1z`);
  }));
  return parts.join("");
}
